/**
 * Shared auth chrome — V4 Channel Atmosphere + glass stage.
 * Visual identity only; pages decide their own forms/logic.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Atmosphere } from './Atmosphere';
import { BrandLogo } from './BrandWordmark';
import { useLanguage } from '../../context/LanguageContext';
import type { Language } from '../../types';

type AuthChromeProps = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

export const AuthChrome: React.FC<AuthChromeProps> = ({
  title,
  subtitle,
  children,
  footer,
}) => {
  const { language, setLanguage, t, direction } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);

  return (
    <div className="bc-auth-shell bc-auth-shell--v4">
      {/* Contained — never position:fixed on the viewport. Fixed atmospheres
          survive React transitions and stack under /customer/welcome after Google. */}
      <Atmosphere mode="channel" signature="focal" contained />

      <div className="absolute top-5 end-5 z-20">
        <div className="relative">
          <button
            type="button"
            onClick={() => setLangOpen((v) => !v)}
            className="bc-auth-lang"
            aria-label={t('auth.language', 'Language')}
            aria-expanded={langOpen}
            aria-controls="auth-language-menu"
          >
            <Globe className="w-4 h-4" />
            <span>{language}</span>
          </button>
          {langOpen && (
            <div
              id="auth-language-menu"
              role="menu"
              className="bc-auth-lang-menu"
            >
              {(
                [
                  ['en', 'English (LTR)'],
                  ['ar', 'العربية (RTL)'],
                  ['ckb', 'کوردی سۆرانی (RTL)'],
                ] as [Language, string][]
              ).map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setLanguage(code);
                    setLangOpen(false);
                  }}
                  className={`bc-auth-lang-item ${
                    language === code ? 'is-active' : ''
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bc-glass bc-glass--auth relative z-10 w-full max-w-md space-y-7" dir={direction}>
        <div className="space-y-4 pb-1">
          <BrandLogo to="/" size="lg" invert />
          <div>
            <h1 className="font-display text-[1.75rem] font-bold text-paper tracking-tight leading-tight">
              {title}
            </h1>
            {subtitle ? (
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{subtitle}</p>
            ) : null}
          </div>
        </div>

        {children}

        {footer ?? (
          <div className="flex justify-between text-xs gap-3 pt-1 border-t border-white/10">
            <Link to="/pricing" className="text-brand-400 hover:text-brand-300 transition-colors">
              {t('nav.contactSales')}
            </Link>
            <Link to="/" className="text-zinc-400 hover:text-paper transition-colors">
              {t('auth.backHome', 'Back home')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
